import { FC, PropsWithChildren, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { Column } from 'further-ui/components/Layout';
import { useSyncScroll } from 'contexts/SyncScrollContext';

const Container = styled(Column)(({ theme }) => ({
  padding: theme.spacing(0, theme.spacingSizes.xxs * 2),
}));

const FooterContent = styled(Box)({
  minWidth: 0,
  width: '100%',
});

const FooterScrollWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isVisible',
})<{ isVisible?: boolean }>(({ isVisible }) => ({
  minWidth: 0,
  width: '100%',
  borderRadius: '100px',
  overflow: 'hidden',
  display: isVisible ? 'block' : 'none',
}));

const FooterScrollContainer = styled(Box)(({ theme }) => ({
  minWidth: 0,
  width: '100%',
  overflowX: 'auto',
  overflowY: 'hidden',
  minHeight: '12px',
  flexShrink: 0,
  borderRadius: '100px',
  '&::-webkit-scrollbar': {
    height: '10px',
    display: 'block',
    borderRadius: '100px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: theme.palette.grey[100],
    borderRadius: '100px',
    margin: '2px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.grey[900],
    borderRadius: '100px',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.grey[900]} ${theme.palette.grey[100]}`,
}));

const FooterScrollSpacer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'minWidth',
})<{ minWidth: number }>(({ minWidth }) => ({
  width: '100%',
  minWidth,
  height: 1,
}));

const AccountingFooter: FC<PropsWithChildren> = ({ children }) => {
  const [footerWidth, setFooterWidth] = useState(0);
  const footerWidthRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { maxScrollWidth, registerScrollContainer, syncScroll } =
    useSyncScroll();

  useEffect(() => {
    const el = footerWidthRef.current;
    if (el == null) {
      return;
    }

    const update = () => setFooterWidth(el.clientWidth);
    update();

    const resizeObserver = new ResizeObserver(() => update());
    resizeObserver.observe(el);
    return () => resizeObserver.disconnect();
  }, []);

  const shouldShowScrollbar =
    maxScrollWidth > 0 && footerWidth > 0 && maxScrollWidth > footerWidth + 1;

  useEffect(() => {
    return registerScrollContainer(scrollRef);
  }, [registerScrollContainer]);

  return (
    <Container spacing={children ? 'lg' : undefined}>
      <FooterContent ref={footerWidthRef}>{children}</FooterContent>
      <FooterScrollWrapper isVisible={shouldShowScrollbar}>
        <FooterScrollContainer
          ref={scrollRef}
          onScroll={(e) => syncScroll(e.currentTarget.scrollLeft, scrollRef)}
        >
          <FooterScrollSpacer minWidth={maxScrollWidth} />
        </FooterScrollContainer>
      </FooterScrollWrapper>
    </Container>
  );
};

export default AccountingFooter;
